import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { RelatedLinks } from "@/components/related-links";
import { articles } from "@/content/articles";
import { coverFor, coverZoomStyle } from "@/content/article-covers";
import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";
import { abs, breadcrumbSchema, PERSON_ID, SITE_URL } from "@/content/seo";

const title = "Psychologia biznesu dla liderów | Sylwia Sitkowska";
const description =
  "Psychologia biznesu z perspektywy liderów i przedsiębiorców. Artykuły o odpowiedzialności, decyzjach, relacjach, delegowaniu, presji i wypaleniu.";
const ogImage = abs(ogSocial);
const PAGE_URL = `${SITE_URL}/psychologia-biznesu`;

const sortedArticles = [...articles].sort(
  (a, b) => new Date(b.datePublished).getTime() - new Date(a.datePublished).getTime()
);

const collectionJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: title,
  description,
  url: PAGE_URL,
  inLanguage: "pl-PL",
  author: { "@id": PERSON_ID },
  mainEntity: {
    "@type": "ItemList",
    itemListElement: sortedArticles.map((article, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: abs(`/${article.slug}`),
      name: article.h1,
    })),
  },
};

const breadcrumbJsonLd = breadcrumbSchema([
  { name: "Strona główna", path: "/" },
  { name: "Psychologia biznesu", path: "/psychologia-biznesu" },
]);

export const Route = createFileRoute("/psychologia-biznesu")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(collectionJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  }),
  component: PsychologiaBiznesuPage,
});

function PsychologiaBiznesuPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="tresc-glowna">
        <section className="border-b border-steel">
          <div className="container-page section-pad">
            <h1 className="serif-display max-w-4xl text-[2.4rem] md:text-[3.4rem]">
              Psychologia biznesu
            </h1>
            <p className="measure mt-8 text-base leading-relaxed text-ink/80 md:text-lg">
              Co dzieje się z człowiekiem, kiedy odpowiada za firmę, ludzi i decyzje? Piszę o
              psychologicznych mechanizmach odpowiedzialności, przywództwa, podejmowania decyzji,
              relacji i funkcjonowania pod presją.
            </p>
          </div>
        </section>

        <section aria-labelledby="artykuly" className="border-b border-steel bg-surface">
          <div className="container-page section-pad">
            <h2 id="artykuly" className="display-xl text-[1.9rem] md:text-[2.6rem]">
              Najnowsze artykuły
            </h2>
            <ul className="stagger mt-12 flex flex-col divide-y divide-steel border-y border-steel">
              {sortedArticles.map((article) => {
                const cover = coverFor(article.slug);
                return (
                  <li key={article.slug}>
                    <Link
                      to={`/${article.slug}` as "/syndrom-oszusta"}
                      className="group grid gap-4 py-6 md:gap-6 md:py-8 transition-colors hover:bg-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-coral md:grid-cols-[minmax(0,18rem)_1fr] md:items-center md:gap-10 md:px-6"
                    >
                      {cover ? (
                        <div className="photo-frame overflow-hidden">
                          <div style={coverZoomStyle(cover)}>
                            <img
                              src={cover.src}
                              alt={cover.alt}
                              loading="lazy"
                              decoding="async"
                              width={1200}
                              height={800}
                              style={{ objectPosition: cover.objectPosition }}
                              className="aspect-[2/1] w-full md:aspect-[3/2] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                            />
                          </div>
                        </div>
                      ) : null}
                      <div>
                        <p className="text-xs uppercase tracking-[0.14em] text-ink-soft">
                          {article.readingMinutes} min czytania
                        </p>
                        <h3 className="mt-3 text-xl font-semibold leading-snug text-ink md:text-2xl">
                          {article.h1}
                        </h3>
                        <p className="measure mt-3 text-base leading-relaxed text-ink/80">
                          {article.teaser}
                        </p>
                        <span className="mt-5 inline-block text-sm font-medium text-coral-text">
                          Czytaj →
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <section aria-labelledby="test-wypalenie" className="border-b border-steel">
          <div className="container-page py-12 md:py-16">
            <div className="flex flex-col gap-6 border border-steel bg-surface p-6 md:flex-row md:items-center md:justify-between md:p-8">
              <div>
                <h2 id="test-wypalenie" className="text-lg font-semibold text-ink">
                  Test na wypalenie zawodowe
                </h2>
                <p className="measure mt-3 text-base leading-relaxed text-ink/80">
                  20 pytań, około 3 minuty, wynik od razu, z podziałem na cztery obszary. Test
                  jest edukacyjny i nie zastępuje konsultacji ze specjalistą.
                </p>
              </div>
              <Link
                to="/test-na-wypalenie-zawodowe"
                className="btn-base btn-primary inline-flex shrink-0 self-start md:self-center"
              >
                Rozpocznij test
              </Link>
            </div>
          </div>
        </section>
      </main>
      <RelatedLinks
        eyebrow="Formy współpracy"
        heading="Jak pracuję z tymi tematami"
        items={[
          {
            to: "/wystapienia",
            title: "Wystąpienia",
            description:
              "Dla firm, konferencji, zarządów i organizacji. Wystąpienia o psychologii liderów, odpowiedzialności, presji, wypaleniu, decyzjach i kosztach sukcesu.",
            cta: "Zobacz wystąpienia",
          },
          {
            to: "/mastermind",
            title: "Mastermind",
            description:
              "Dla przedsiębiorców, właścicieli firm i liderów. Poufna przestrzeń do rozmowy z osobami na podobnym poziomie odpowiedzialności.",
            cta: "Poznaj mastermind",
          },
          {
            to: "/konsultacje",
            title: "Konsultacje indywidualne",
            description: "Dla osób, których sytuacja wymaga indywidualnej, poufnej pracy.",
            cta: "Sprawdź konsultacje",
          },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
